/**
 * 安全页面区域适配工具函数
 *
 * 根据运营区域（国内/海外）判断安全设置项和第三方账号的显示逻辑
 *
 * 显示规则：
 * - 国内模式：显示手机号、微信
 * - 海外模式：显示邮箱、Google、Apple
 * - 密码：始终显示
 * - 跨区域历史绑定：只读显示
 */

/**
 * 安全项类型
 */
export type SecurityItemType =
  | "password"
  | "phone"
  | "email"
  | "wechat"
  | "google"
  | "apple";

/**
 * 运营区域类型
 */
export type OperationRegion = "domestic" | "international";

/**
 * 判断安全项是否应该显示
 *
 * 显示规则：
 * 1. 密码：始终显示
 * 2. 手机号：国内模式显示，或海外模式下有历史绑定时显示（只读）
 * 3. 邮箱：海外模式显示，或国内模式下有历史绑定时显示（只读）
 * 4. 微信：国内模式显示，或海外模式下有历史绑定时显示（只读）
 * 5. Google：海外模式显示，或国内模式下有历史绑定时显示（只读）
 * 6. Apple：海外模式显示，或国内模式下有历史绑定时显示（只读）
 *
 * @param itemType - 安全项类型
 * @param isDomestic - 是否国内模式
 * @param hasExistingBinding - 是否有历史绑定
 * @returns 是否显示该项
 */
export function shouldShowSecurityItem(
  itemType: SecurityItemType,
  isDomestic: boolean,
  hasExistingBinding: boolean
): boolean {
  switch (itemType) {
    case "password":
      // 密码始终显示
      return true;

    case "phone":
      // 国内模式显示，或海外模式下有历史绑定时显示
      return isDomestic || hasExistingBinding;

    case "email":
      // 海外模式显示，或国内模式下有历史绑定时显示
      return !isDomestic || hasExistingBinding;

    case "wechat":
      // 国内模式显示，或海外模式下有历史绑定时显示
      return isDomestic || hasExistingBinding;

    case "google":
    case "apple":
      // 海外模式显示，或国内模式下有历史绑定时显示
      return !isDomestic || hasExistingBinding;

    default:
      return false;
  }
}

/**
 * 判断安全项是否为只读模式
 *
 * 只读规则：
 * 当安全项不属于当前区域但有历史绑定时，显示为只读
 * - 手机号在海外模式下有绑定：只读
 * - 邮箱在国内模式下有绑定：只读
 * - 微信在海外模式下有绑定：只读
 * - Google/Apple在国内模式下有绑定：只读
 *
 * @param itemType - 安全项类型
 * @param isDomestic - 是否国内模式
 * @param hasExistingBinding - 是否有历史绑定
 * @returns 是否只读
 */
export function isReadOnlyItem(
  itemType: SecurityItemType,
  isDomestic: boolean,
  hasExistingBinding: boolean
): boolean {
  // 没有绑定的项不需要只读
  if (!hasExistingBinding) {
    return false;
  }

  switch (itemType) {
    case "password":
      // 密码永远不是只读
      return false;

    case "phone":
      // 手机号在海外模式下有绑定时只读
      return !isDomestic && hasExistingBinding;

    case "email":
      // 邮箱在国内模式下有绑定时只读
      return isDomestic && hasExistingBinding;

    case "wechat":
      // 微信在海外模式下有绑定时只读
      return !isDomestic && hasExistingBinding;

    case "google":
    case "apple":
      // Google/Apple在国内模式下有绑定时只读
      return isDomestic && hasExistingBinding;

    default:
      return false;
  }
}

/**
 * 判断安全项是否属于当前区域
 *
 * @param itemType - 安全项类型
 * @param isDomestic - 是否国内模式
 * @returns 是否属于当前区域
 */
export function isItemInCurrentRegion(
  itemType: SecurityItemType,
  isDomestic: boolean
): boolean {
  switch (itemType) {
    case "password":
      // 密码属于所有区域
      return true;

    case "phone":
    case "wechat":
      // 手机号和微信属于国内区域
      return isDomestic;

    case "email":
    case "google":
    case "apple":
      // 邮箱、Google、Apple属于海外区域
      return !isDomestic;

    default:
      return false;
  }
}
